import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserCircle, LogOut, Trash2, ScrollText, Sparkles, X, Pencil, MailCheck, KeyRound, MonitorSmartphone } from "lucide-react";
import { api } from "../lib/api";
import { supabase } from "../lib/supabaseClient";
import { ACCOUNT } from "../constants/testIds";

// Phase 6.2 account lifecycle + legal re-acceptance + first-login onboarding.
// Item 6: "Edit my details" — display name (immediate) + email (Supabase secure
// email-change; current email stays active until the new address is confirmed).
export function AccountArea({ me, onLogout, onChanged }) {
  const [status, setStatus] = useState({ outstanding_registration: [], outstanding_contribution: [] });
  const [name, setName] = useState(me?.display_name || "");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const key = `ww_onboarded_${me?.www_user_id}`;
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem(key));
  // Item 13 — Change password (Supabase Auth; never touches our backend/DB).
  const [cpwOpen, setCpwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confPw, setConfPw] = useState("");
  const [cpwBusy, setCpwBusy] = useState(false);

  // Phase 0: "Your devices" — linked TVs/screens. Self-hides when DEVICE_API_ENABLED
  // is off (endpoints 404), so existing web behaviour is unchanged while the flag is off.
  const [devices, setDevices] = useState(null); // null=unknown, false=feature off, []=none
  useEffect(() => {
    (async () => {
      const r = await api("/account/devices");
      if (r.ok) setDevices(r.data.devices || []);
      else if (r.status === 404) setDevices(false);
    })();
  }, []);
  const revokeDevice = async (id) => {
    const r = await api(`/account/devices/${id}/revoke`, { method: "POST" });
    if (r.ok) { toast.success("Device unlinked"); setDevices((d) => d.filter((x) => x.id !== id)); }
    else toast.error("Could not unlink device");
  };
  const renameDevice = async (id, name) => {
    const r = await api(`/account/devices/${id}`, { method: "PATCH", body: { name } });
    if (r.ok) { toast.success("Renamed"); setDevices((d) => d.map((x) => x.id === id ? { ...x, name } : x)); }
    else toast.error("Rename failed");
  };
  const [devEditId, setDevEditId] = useState(null);
  const [devEditName, setDevEditName] = useState("");
  const [devConfirmId, setDevConfirmId] = useState(null);
  const startRename = (d) => { setDevEditId(d.id); setDevEditName(d.name); };
  const commitRename = async (id) => {
    const n = devEditName.trim();
    if (n) await renameDevice(id, n);
    setDevEditId(null); setDevEditName("");
  };

  const resetCpw = () => { setCpwOpen(false); setCurPw(""); setNewPw(""); setConfPw(""); };
  const changePassword = async () => {
    if (newPw.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPw !== confPw) return toast.error("New passwords do not match");
    setCpwBusy(true);
    try {
      // MFA-enrolled accounts must be AAL2 (verified) before changing the password.
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.currentLevel === "aal1" && aal?.nextLevel === "aal2") {
        return toast.error("Please complete MFA verification before changing your password.");
      }
      const { error } = await supabase.auth.updateUser({ password: newPw, current_password: curPw });
      if (error) toast.error(error.message || "Could not change password");
      else { toast.success("Password changed."); resetCpw(); }
    } finally { setCpwBusy(false); }
  };

  const loadStatus = useCallback(async () => {
    const r = await api("/me/legal-status");
    if (r.ok) setStatus(r.data);
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { setName(me?.display_name || ""); }, [me]);

  const dismissWelcome = () => { localStorage.setItem(key, "1"); setShowWelcome(false); };

  const saveName = async () => {
    setBusy(true);
    const r = await api("/me", { method: "PATCH", body: { display_name: name || null } });
    setBusy(false);
    if (r.ok) { toast.success("Display name updated"); onChanged?.(); } else toast.error("Could not update profile");
  };

  const changeEmail = async () => {
    const e = newEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return toast.error("Enter a valid email address");
    if (e === (me?.email || "").toLowerCase()) return toast.error("That is already your email address");
    setEmailBusy(true);
    const { error } = await supabase.auth.updateUser(
      { email: e }, { emailRedirectTo: `${window.location.origin}/` });
    setEmailBusy(false);
    if (error) return toast.error(error.message || "Could not start the email change");
    setEmailSent(true);
    setNewEmail("");
  };

  const closeEdit = () => {
    setEditing(false); setEmailSent(false); setNewEmail(""); setName(me?.display_name || ""); setCancelConfirm(false);
  };
  const hasUnsaved = () => (name !== (me?.display_name || "")) || newEmail.trim() !== "";
  const onCancel = () => { if (hasUnsaved()) setCancelConfirm(true); else closeEdit(); };

  const accept = async (doc) => {
    const r = await api("/me/legal/accept", { method: "POST", body: { doc_type: doc.doc_type, version: doc.version } });
    if (r.ok) { toast.success(`${doc.title.replace(/\s*\(DRAFT\)/, "")} recorded`); loadStatus(); }
    else toast.error(r.data?.detail || "Could not record acceptance");
  };

  const del = async () => {
    setBusy(true);
    const r = await api("/me/delete", { method: "POST", body: { confirm: true } });
    setBusy(false);
    if (r.ok) {
      toast.success(r.data.message || "Account deleted");
      try { await supabase.auth.signOut(); } finally {
        setTimeout(() => { window.location.href = "/"; }, 900);
      }
    } else toast.error(r.data?.detail || "Deletion failed");
  };

  const betaDue = status.outstanding_registration || [];

  return (
    <div data-testid={ACCOUNT.area}>
      {showWelcome && (
        <div className="card" data-testid={ACCOUNT.onboarding}
             style={{ borderColor: "var(--ww-accent, #c65f3f)", position: "relative" }}>
          <button className="btn ghost" onClick={dismissWelcome} data-testid={ACCOUNT.onboardingDismiss}
                  aria-label="Dismiss welcome message"
                  style={{ position: "absolute", top: 10, right: 10, padding: 6 }}><X size={14} /></button>
          <h3><Sparkles size={16} /> Welcome to World Wide Wallpaper</h3>
          <p className="muted">
            A world of joy, quietly shared. Set the world in motion, and when you're ready,
            accept the Contributor Terms below and share a photograph of your own — every
            submission is reviewed before it appears.
          </p>
        </div>
      )}

      {betaDue.map((doc) => (
        <div className="card" data-testid={ACCOUNT.betaReaccept} key={doc.id}
             style={{ borderColor: "#c65f3f" }}>
          <h3><ScrollText size={16} /> Please review our updated terms</h3>
          <p className="muted" style={{ whiteSpace: "pre-wrap" }}>{doc.body}</p>
          <button className="btn primary" data-testid={ACCOUNT.betaReacceptButton} onClick={() => accept(doc)}>
            Accept {doc.title.replace(/\s*\(DRAFT\)/, "")} ({doc.version})
          </button>
        </div>
      ))}

      <div className="card" data-testid={ACCOUNT.card}>
        <h3><UserCircle size={16} /> Your account</h3>

        {!editing ? (
          <>
            <div className="kv"><span>Email</span><b>{me?.email || "—"}</b></div>
            <div className="kv"><span>Display name</span><b>{me?.display_name || "—"}</b></div>
            <div className="row wrap" style={{ marginTop: 12 }}>
              <button className="btn" data-testid={ACCOUNT.editButton}
                      onClick={() => { setEditing(true); setEmailSent(false); }}>
                <Pencil size={13} /> Edit my details
              </button>
              <button className="btn ghost" data-testid={ACCOUNT.logoutButton} onClick={onLogout}>
                <LogOut size={13} /> Log out
              </button>
              {!confirming ? (
                <button className="btn danger" data-testid={ACCOUNT.deleteButton} onClick={() => setConfirming(true)}>
                  <Trash2 size={13} /> Delete account
                </button>
              ) : (
                <>
                  <span className="muted small">Permanently delete your account &amp; photographs?</span>
                  <button className="btn danger" data-testid={ACCOUNT.deleteConfirmButton} disabled={busy} onClick={del}>
                    Yes, delete
                  </button>
                  <button className="btn" data-testid={ACCOUNT.deleteCancelButton} onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <label className="field-label">Display name</label>
            <input data-testid={ACCOUNT.displayNameInput} value={name}
                   onChange={(e) => setName(e.target.value)} placeholder="Your display name" />
            <p className="muted small" style={{ margin: "-4px 0 12px" }}>
              This will be proudly displayed with your submitted pictures.
            </p>
            <button className="btn" data-testid={ACCOUNT.saveButton} disabled={busy} onClick={saveName}>
              Save display name
            </button>

            <hr style={{ border: "none", borderTop: "1px solid var(--ww-line)", margin: "18px 0" }} />

            <label className="field-label">Email address</label>
            <div className="kv"><span>current</span><b>{me?.email || "—"}</b></div>
            {emailSent ? (
              <p className="muted" data-testid={ACCOUNT.emailSent} style={{ marginTop: 8 }}>
                <MailCheck size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                We've sent a verification link to your new email address. Your current email will
                remain active until verification is complete.
              </p>
            ) : (
              <>
                <input data-testid={ACCOUNT.emailInput} type="email" value={newEmail}
                       onChange={(e) => setNewEmail(e.target.value)} placeholder="New email address" />
                <p className="muted small" style={{ margin: "-4px 0 12px" }}>
                  We'll email a verification link to your new address. Your current email stays your
                  login until you confirm the change.
                </p>
                <button className="btn primary" data-testid={ACCOUNT.emailChangeButton}
                        disabled={emailBusy} onClick={changeEmail}>
                  {emailBusy ? "…" : "Send verification link"}
                </button>
              </>
            )}

            <div className="row wrap" style={{ marginTop: 16, flexDirection: "column", alignItems: "stretch", gap: 8 }}>
              {!cancelConfirm ? (
                <button className="btn ghost" data-testid={ACCOUNT.editCancel} onClick={onCancel}>
                  Cancel
                </button>
              ) : (
                <div className="callout-warn" data-testid="account-cancel-confirm"
                     style={{ border: "1px solid var(--ww-line)", borderRadius: 8, padding: "12px 14px" }}>
                  <p className="muted small" style={{ margin: "0 0 10px" }}>
                    Are you sure? Any unsaved changes will be lost.
                  </p>
                  <div className="row wrap" style={{ gap: 8 }}>
                    <button className="btn danger" data-testid="account-cancel-confirm-yes" onClick={closeEdit}>
                      Discard changes
                    </button>
                    <button className="btn" data-testid="account-cancel-confirm-no" onClick={() => setCancelConfirm(false)}>
                      Keep editing
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="card" data-testid="change-password-card">
        <h3><KeyRound size={16} /> Change password</h3>
        {!cpwOpen ? (
          <button className="btn" data-testid="change-password-open" onClick={() => setCpwOpen(true)}>
            <KeyRound size={13} /> Change password
          </button>
        ) : (
          <>
            <label className="field-label">Current password</label>
            <input type="password" data-testid="cpw-current" value={curPw}
                   onChange={(e) => setCurPw(e.target.value)} autoComplete="current-password" />
            <label className="field-label">New password</label>
            <input type="password" data-testid="cpw-new" value={newPw}
                   onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters" />
            <label className="field-label">Confirm new password</label>
            <input type="password" data-testid="cpw-confirm" value={confPw}
                   onChange={(e) => setConfPw(e.target.value)} autoComplete="new-password" />
            <div className="row wrap" style={{ marginTop: 12 }}>
              <button className="btn primary" data-testid="cpw-submit" disabled={cpwBusy} onClick={changePassword}>
                {cpwBusy ? "…" : "Update password"}
              </button>
              <button className="btn ghost" data-testid="cpw-cancel" onClick={resetCpw}>Cancel</button>
            </div>
          </>
        )}
      </div>

      {Array.isArray(devices) && (
        <div className="card" data-testid="account-devices">
          <h3>Your devices</h3>
          <p className="muted small">Screens linked to your WWW account (TVs and other displays). Unlink any you no longer use.</p>
          {devices.length === 0 && (
            <p className="muted" data-testid="devices-empty">No devices linked yet. On a TV, open the World Wide Wallpaper slideshow and scan the QR code with this phone — or tap “Link a device” below to enter the code by hand.</p>
          )}
          <ul className="list">
            {devices.map((d) => (
              <li key={d.id} className="kv" data-testid={`device-${d.id}`} style={{ alignItems: "center", flexWrap: "wrap", gap: 8, rowGap: 10 }}>
                {devEditId === d.id ? (
                  <span className="row wrap" style={{ gap: 8, flex: 1 }}>
                    <input data-testid={`device-name-input-${d.id}`} value={devEditName} autoFocus
                           onChange={(e) => setDevEditName(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter") commitRename(d.id); if (e.key === "Escape") setDevEditId(null); }}
                           style={{ maxWidth: 220 }} maxLength={60} />
                    <button className="btn small primary" data-testid={`device-rename-save-${d.id}`} onClick={() => commitRename(d.id)}>Save</button>
                    <button className="btn small ghost" data-testid={`device-rename-cancel-${d.id}`} onClick={() => setDevEditId(null)}>Cancel</button>
                  </span>
                ) : (
                  <>
                    <span style={{ flex: "1 1 220px", minWidth: 0, overflowWrap: "anywhere" }}><b>{d.name}</b> <span className="muted small">· {d.platform || "device"} · last seen {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString("en-GB") : "never"}</span></span>
                    <span className="row wrap" style={{ marginBottom: 0, flexShrink: 0 }}>
                      {devConfirmId === d.id ? (
                        <>
                          <span className="muted small">Unlink this device?</span>
                          <button className="btn small danger" data-testid={`device-revoke-confirm-${d.id}`}
                                  onClick={() => { revokeDevice(d.id); setDevConfirmId(null); }}>Yes, unlink</button>
                          <button className="btn small" data-testid={`device-revoke-cancel-${d.id}`}
                                  onClick={() => setDevConfirmId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="btn small" data-testid={`device-rename-${d.id}`} onClick={() => startRename(d)}>Rename</button>
                          <button className="btn small danger" data-testid={`device-revoke-${d.id}`} onClick={() => setDevConfirmId(d.id)}>Unlink</button>
                        </>
                      )}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn" data-testid="devices-link-new" onClick={() => { window.location.href = "/link"; }}>
              <MonitorSmartphone size={13} /> Link a device
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
